import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Spinner, VTooltip } from "@vertesia/ui/core";
import { useUserSession } from "@vertesia/ui/session";
import { Maximize2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useUITranslation } from '../../i18n/index.js';
import { PdfPageSlider } from "./PdfPageSlider";
/**
 * A standalone PDF viewer component that displays PDF thumbnails with navigation.
 * Fetches the PDF URL from the content object and displays it using PdfThumbnailSlider.
 * Does not depend on any magic-pdf context.
 */
export function SimplePdfViewer({ object, url, source, className }) {
    const { t } = useUITranslation();
    const { client } = useUserSession();
    const [currentPage, setCurrentPage] = useState(1);
    const [pdfUrl, setPdfUrl] = useState(url || "");
    const [pdfUrlLoading, setPdfUrlLoading] = useState(!url);
    const [pageCount, setPageCount] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // Fetch the PDF URL - priority: url > source > object.content.source
    useEffect(() => {
        // If url prop is provided, use it directly (already signed)
        if (url) {
            setPdfUrl(url);
            setPdfUrlLoading(false);
            return;
        }
        // Determine the source to resolve
        const sourceToResolve = source || object?.content?.source;
        if (!sourceToResolve) {
            setPdfUrlLoading(false);
            return;
        }
        setPdfUrlLoading(true);
        client.files.getDownloadUrl(sourceToResolve)
            .then((result) => {
            setPdfUrl(result.url);
        })
            .catch((err) => {
            console.error("Failed to get PDF URL:", err);
        })
            .finally(() => {
            setPdfUrlLoading(false);
        });
    }, [url, source, object?.content?.source, client]);
    // Get initial page count from metadata, but actual count will be updated when PDF loads
    useEffect(() => {
        // Try to get page count from metadata as initial estimate
        const metadata = object?.metadata;
        const count = metadata?.pages || metadata?.page_count || 0;
        if (count > 0) {
            setPageCount(count);
        }
        // If no metadata, keep pageCount at 0 - it will be set when PDF loads
    }, [object?.metadata]);
    // Callback to update page count when PDF loads
    const handlePageCountChange = (count) => {
        setPageCount(count);
    };
    if (pdfUrlLoading) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (!pdfUrl) {
        return (_jsx("div", { className: "flex items-center justify-center h-full text-muted", children: t('pdf.noPdfAvailable') }));
    }
    // Fullscreen overlay
    if (isFullscreen) {
        return (_jsxs("div", { className: "fixed inset-0 bg-background z-50 flex flex-col overflow-hidden", children: [_jsx("div", { className: "flex h-9 items-center justify-end shrink-0 bg-sidebar px-2 border-b border-sidebar-border", children: _jsx(Button, { variant: "ghost", size: "xs", onClick: () => setIsFullscreen(false), alt: t('pdf.closeFullscreen'), children: _jsx(X, { className: "size-4" }) }) }), _jsx(PdfPageSlider, { pdfUrl: pdfUrl, pdfUrlLoading: pdfUrlLoading, pageCount: pageCount || 100, currentPage: currentPage, onChange: setCurrentPage, onPageCountChange: handlePageCountChange, className: "flex-1 min-h-0" })] }));
    }
    return (_jsx("div", { className: "relative h-full flex flex-col", children: _jsx(PdfPageSlider, { pdfUrl: pdfUrl, pdfUrlLoading: pdfUrlLoading, pageCount: pageCount || 100, currentPage: currentPage, onChange: setCurrentPage, onPageCountChange: handlePageCountChange, className: className, compact: true, headerExtra: _jsx(VTooltip, { description: t('pdf.fullscreen'), placement: "bottom", size: "xs", children: _jsx("button", { className: "p-1 rounded cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:bg-muted", onClick: () => setIsFullscreen(true), children: _jsx(Maximize2, { className: "size-4" }) }) }) }) }));
}
//# sourceMappingURL=SimplePdfViewer.js.map